/*
  Admin — company settings, people & access, stage template, history and integrations.
  Waves 1–2 of the UI overhaul (screens 26–31). The page owns the shared frame: header (with the
  per-tab action slot), the tab bar (navy underline, one icon per tab, counts, amber "needs a look" dots,
  the tab kept in the URL as ?tab=…) and the data the tab bar needs. Each tab lives in
  components/admin/*. History loads only when its tab is opened. Unsaved work (Settings, the Access
  draft, Edit pipeline) asks before the tab or the page is left.
  Final QA: a ribbon under the top bar while a theme is previewed (26 #2, "Exit preview" puts the draft back), a
  "N tabs need a look" chip after the tabs that lists why (26 #6 — the amber dots alone are hover-only), and a tab
  bar that fits 1024px or fades at the edge it scrolls to.
*/
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowRight, ChevronDown, ChevronRight, CircleCheck, Download, Eye, History, Plug, Plus, RefreshCw, ShieldCheck, SlidersHorizontal,
  SquarePen, UserPlus, Users, Workflow, X,
} from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { readStored, writeStored } from '@/lib/storage';
import { THEME_OPTIONS, useAppSettings } from '@/contexts/AppSettingsContext';
import { useAuth } from '@/contexts/AuthContext';
import { PERMISSION_MODULES, SUPER_ADMIN_ROLE } from '@/lib/permissions';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PageHeader } from '@/components/ui/page-header';
import { useBreadcrumbs } from '@/components/ui/breadcrumbs';
import { notify } from '@/components/ui/notify';
import SettingsTab from '@/components/admin/SettingsTab';
import UsersTab, { AddUserPanel } from '@/components/admin/UsersTab';
import AccessTab from '@/components/admin/AccessTab';
import StagesTab from '@/components/admin/StagesTab';
import HistoryTab from '@/components/admin/HistoryTab';
import IntegrationsTab from '@/components/admin/IntegrationsTab';
import {
  DEFAULT_HISTORY_FILTERS, HEALTH_DISMISS_KEY, accountHealth, parseAdminTab, quickBooksState,
} from '@/components/admin/adminModel';

const USER_ROLE_OPTIONS = [
  'Super Admin',
  'Admin',
  'PM',
  'Presales',
  'Accountant',
  'Storekeeper',
  'Site Engineer',
  'Senior Site Engineer',
  'Site Supervisor',
  'Technician',
  'Backoffice Engineer',
  'Viewer'
];

const tabTriggerClass = 'group relative h-auto shrink-0 gap-1.5 rounded-none bg-transparent px-0 pb-[11px] pt-2.5 text-[13.5px] font-semibold text-ink-3 shadow-none ring-offset-0 hover:text-ink-2 data-[state=active]:bg-transparent data-[state=active]:text-ink data-[state=active]:shadow-none after:absolute after:inset-x-0 after:-bottom-px after:h-[2.5px] after:rounded-t after:bg-transparent data-[state=active]:after:bg-brand [&>svg]:h-[15px] [&>svg]:w-[15px]';
// counts step aside between 1024 and 1279px so all six tabs (and the "need a look" chip) fit beside the sidebar
const tabCountClass = 'rounded-full bg-[color:var(--status-gray-bg)] px-[7px] py-px text-[11px] font-bold tabular-nums text-ink-2 group-data-[state=active]:bg-brand group-data-[state=active]:text-brand-fg lg:max-xl:hidden';

function TabWarn({ title }) {
  return (
    <>
      <span className="ui-tone-dot tone-amber h-[7px] w-[7px]" title={title} aria-hidden="true" />
      <span className="sr-only">Needs a look: {title}</span>
    </>
  );
}

/** 26 #2 — while a theme is previewed: a slim ribbon under the top bar, on every scroll position of the page. */
function PreviewRibbon({ theme, onExit }) {
  const label = THEME_OPTIONS.find((option) => option.value === theme)?.label || theme;
  return (
    <div
      role="status"
      className="sticky top-[var(--app-header-height)] z-20 -mx-4 -mt-6 mb-2 flex min-h-[38px] items-center gap-2 border-b border-brand-soft-border bg-surface bg-[linear-gradient(var(--brand-soft),var(--brand-soft))] px-4 py-2 text-[12.5px] text-brand-ink md:-mx-6 md:-mt-8 md:mb-4 md:px-6 lg:-mx-8 lg:-mt-10 lg:mb-6 lg:px-8"
      data-testid="theme-preview-ribbon"
    >
      <Eye className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        Previewing <b className="font-bold text-ink">{label}</b> — only you see this until you save
      </span>
      <button
        type="button"
        onClick={onExit}
        className="inline-flex shrink-0 items-center gap-1 rounded font-semibold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid="theme-preview-exit"
      >
        <X className="h-[13px] w-[13px]" aria-hidden="true" /> Exit preview
      </button>
    </div>
  );
}

/** 26 #6 — "3 tabs need a look": the reasons behind the amber dots, readable by touch and by sight. */
function AttentionChip({ tabs, onPick }) {
  const [open, setOpen] = useState(false);
  const warned = tabs.filter((item) => item.warn);
  if (!warned.length) return null;
  const label = `${warned.length} ${warned.length === 1 ? 'tab needs' : 'tabs need'} a look`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group mb-1.5 ml-2 inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2 text-[12.5px] font-semibold text-ink-2 transition-colors hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-surface-hover"
          aria-label={label}
          data-testid="admin-attention"
        >
          <span className="ui-tone-dot tone-amber h-[7px] w-[7px]" aria-hidden="true" />
          <span className="hidden xl:inline" aria-hidden="true">{label}</span>
          <span className="tabular-nums xl:hidden" aria-hidden="true">{warned.length}</span>
          <ChevronDown className="h-3.5 w-3.5 text-ink-3 transition-transform group-data-[state=open]:rotate-180" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(380px,calc(100vw-2rem))] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay"
        data-testid="admin-attention-list"
      >
        <p className="px-2 pb-1 pt-0.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Needs a look</p>
        <ul>
          {warned.map((item) => (
            <li key={item.value}>
              <button
                type="button"
                onClick={() => { setOpen(false); onPick(item.value); }}
                className="flex min-h-[34px] w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-[12.5px] transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
                data-testid={`admin-attention-${item.value}`}
              >
                <span className="ui-tone-dot tone-amber h-[7px] w-[7px] shrink-0" aria-hidden="true" />
                <b className="w-[112px] shrink-0 font-bold text-ink">{item.label}</b>
                <span className="min-w-0 flex-1 text-ink-3">{item.warn}</span>
                <ArrowRight className="h-3.5 w-3.5 shrink-0 text-ink-4" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

const EDGE_FADE = 40;
const tabMask = ({ left, right }) => {
  if (!left && !right) return undefined;
  const stops = [left ? `transparent, black ${EDGE_FADE}px` : 'black', right ? `black calc(100% - ${EDGE_FADE}px), transparent` : 'black'];
  return `linear-gradient(to right, ${stops.join(', ')})`;
};

const rolesFromResponse = (data) => ({
  roles: data?.roles || USER_ROLE_OPTIONS,
  modules: data?.modules || PERMISSION_MODULES.map((item) => item.key),
  permissions: data?.permissions || {},
  role_details: data?.role_details || {},
  can_manage_permissions: Boolean(data?.can_manage_permissions),
  // 28 #8 View / Edit levels
  view_only: data?.view_only || {},
  level_modules: data?.level_modules || null,
  level_modules_soon: data?.level_modules_soon || null,
  read_only_roles: data?.read_only_roles || null,
  // 27 #9: turning sign-in off only once the sign-in paths refuse a disabled account
  sign_in_off_enforced: Boolean(data?.sign_in_off_enforced),
  // since when sign-ins are recorded: "Never signed in" only for accounts created after it
  sign_in_tracking_since: data?.sign_in_tracking_since || '',
});

/** Top bar: "Tools / Admin / History". */
function AdminCrumbs({ label }) {
  useBreadcrumbs([label]);
  return null;
}

export default function AdminPage() {
  const { user: currentUser } = useAuth();
  const { appName, settings, workspace, previewTheme, stopPreview } = useAppSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseAdminTab(searchParams.get('tab'));

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [roleAccess, setRoleAccess] = useState(() => rolesFromResponse(null));
  const [stageTemplate, setStageTemplate] = useState(null);
  const [stageLoading, setStageLoading] = useState(true);
  const [quickBooksStatus, setQuickBooksStatus] = useState(null);
  const [quickBooksLoading, setQuickBooksLoading] = useState(true);
  const [quickBooksError, setQuickBooksError] = useState('');
  const [integrations, setIntegrations] = useState(null);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [historyFilters, setHistoryFilters] = useState(DEFAULT_HISTORY_FILTERS);
  const [historyExport, setHistoryExport] = useState(0);
  const [settingsDirty, setSettingsDirty] = useState(0);
  const [accessDirty, setAccessDirty] = useState(0);
  const [stagesDirty, setStagesDirty] = useState(0);
  const [stagesEditing, setStagesEditing] = useState(false);
  const [stageCommand, setStageCommand] = useState(null);
  // "Recheck status": the Integrations tab reloads its activity only when this changes (not on every status load)
  const [recheckSeq, setRecheckSeq] = useState(0);
  const [tabEdges, setTabEdges] = useState({ left: false, right: false });
  // This browser's "Dismiss" of the Users account-health callout (the set of test admins it was for).
  const [healthDismissed, setHealthDismissed] = useState(() => readStored(HEALTH_DISMISS_KEY, ''));
  const dismissHealth = useCallback((signature) => {
    writeStored(HEALTH_DISMISS_KEY, signature);
    setHealthDismissed(signature);
  }, []);

  // Unsaved Settings / Access draft / pipeline edits: closing the page, in-app links and switching tabs
  // (a URL change) all ask first.
  useUnsavedChanges(settingsDirty + accessDirty + stagesDirty > 0);

  const refreshUsers = useCallback(async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      notify.error('Could not load people', { description: getErrorMessage(err) });
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const refreshRoles = useCallback(async () => {
    const res = await api.get('/admin/roles');
    setRoleAccess(rolesFromResponse(res.data));
  }, []);

  // People were added, deleted or re-roled (or that was undone): the Access tab's "N users" follow.
  const onPeopleChanged = useCallback(() => { refreshRoles().catch(() => {}); }, [refreshRoles]);

  const refreshStageTemplate = useCallback(async () => {
    try {
      const res = await api.get('/admin/stage-templates');
      setStageTemplate(res.data);
    } catch (err) {
      setStageTemplate(null);
    } finally {
      setStageLoading(false);
    }
  }, []);

  const refreshQuickBooks = useCallback(async () => {
    setQuickBooksLoading(true);
    try {
      const res = await api.get('/quickbooks/status');
      setQuickBooksStatus(res.data);
      setQuickBooksError('');
    } catch (err) {
      setQuickBooksStatus(null);
      setQuickBooksError(getErrorMessage(err, 'The status check failed'));
    } finally {
      setQuickBooksLoading(false);
    }
  }, []);

  // OneDrive coverage + QuickBooks invoice syncs (Integrations cards, the tab's count).
  const refreshIntegrations = useCallback(async () => {
    setIntegrationsLoading(true);
    try {
      const res = await api.get('/admin/integrations');
      setIntegrations(res.data || null);
    } catch {
      setIntegrations(null);
    } finally {
      setIntegrationsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUsers();
    refreshRoles().catch(() => {});
    refreshStageTemplate();
    refreshQuickBooks();
    refreshIntegrations();
  }, [refreshUsers, refreshRoles, refreshStageTemplate, refreshQuickBooks, refreshIntegrations]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'quickbooks-connected') {
        notify.success('QuickBooks connected');
        refreshQuickBooks();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [refreshQuickBooks]);

  const switchTab = useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Phones: the tab bar scrolls sideways — keep the open tab in view (e.g. after ?tab=integrations).
  const tabListRef = useRef(null);
  useEffect(() => {
    const list = tabListRef.current;
    const active = list?.querySelector('[role="tab"][data-state="active"]');
    if (!list || !active || list.scrollWidth <= list.clientWidth) return;
    // counts arrive after the first paint and widen the tabs — re-centre when they do
    const offset = active.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
    list.scrollLeft = Math.max(0, offset - (list.clientWidth - active.offsetWidth) / 2);
  }, [tab, usersLoading, stageTemplate, quickBooksStatus]);

  // Where the tab bar can still scroll: that edge fades (and gets a ›), so a clipped "Integrati…" says "more this way".
  const measureTabEdges = useCallback(() => {
    const list = tabListRef.current;
    if (!list) return;
    const next = {
      left: list.scrollLeft > 2,
      right: list.scrollLeft + list.clientWidth < list.scrollWidth - 2,
    };
    setTabEdges((current) => (current.left === next.left && current.right === next.right ? current : next));
  }, []);
  useLayoutEffect(() => {
    const list = tabListRef.current;
    if (!list) return undefined;
    measureTabEdges();
    list.addEventListener('scroll', measureTabEdges, { passive: true });
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measureTabEdges) : null;
    observer?.observe(list);
    window.addEventListener('resize', measureTabEdges);
    return () => {
      list.removeEventListener('scroll', measureTabEdges);
      observer?.disconnect();
      window.removeEventListener('resize', measureTabEdges);
    };
  }, [measureTabEdges]);
  useEffect(() => { measureTabEdges(); }, [measureTabEdges, tab, usersLoading, stageTemplate, quickBooksStatus]);

  const openHistory = useCallback((patch) => {
    setHistoryFilters({ ...DEFAULT_HISTORY_FILTERS, ...patch });
    switchTab('history');
  }, [switchTab]);

  const health = useMemo(() => accountHealth(users), [users]);
  // the amber dot and the callout hide together once dismissed for the same test accounts
  const healthWarn = health.needsLook && healthDismissed !== health.signature;
  const qbState = quickBooksState(quickBooksStatus);
  const onedrive = integrations?.onedrive;
  const onedriveWorking = Boolean(onedrive?.configured && onedrive?.linked > 0);
  const integrationsConnected = (qbState === 'connected' ? 1 : 0) + (onedriveWorking ? 1 : 0);
  const stageOutOfDate = stageTemplate?.sync?.in_use === false;
  const viewerIsSuperAdmin = currentUser?.role === SUPER_ADMIN_ROLE;
  const recheckIntegrations = useCallback(() => {
    refreshQuickBooks();
    refreshIntegrations();
    setRecheckSeq((n) => n + 1);
  }, [refreshQuickBooks, refreshIntegrations]);

  const integrationsWarn = (() => {
    if (quickBooksError && !quickBooksStatus) return 'Couldn’t check QuickBooks';
    if (!quickBooksStatus || integrationsConnected >= 2) return '';
    const reasons = [
      qbState === 'setup' ? 'QuickBooks needs server setup' : qbState !== 'connected' ? 'QuickBooks is not connected' : '',
      onedrive && !onedrive.configured ? 'OneDrive needs server setup' : onedrive && !onedriveWorking ? 'No project is linked to OneDrive' : '',
    ].filter(Boolean);
    return reasons.join(' · ') || 'Nothing connected yet';
  })();

  const headerActions = {
    settings: settings?.updated_at ? (
      <p className="flex max-w-[calc(100vw-2rem)] flex-wrap items-center gap-x-1.5 text-[12.5px] text-ink-3 md:max-w-none" data-testid="settings-saved-line">
        <CircleCheck className="h-3.5 w-3.5 text-status-green" aria-hidden="true" />
        <span className="text-ink-2">
          Saved {formatDateTime(settings.updated_at, { timeZone: workspace?.time_zone })} {workspace?.time_zone_short || 'GST'}
        </span>
        {settings.updated_by ? <span>· by {settings.updated_by}</span> : null}
        <span aria-hidden="true">·</span>
        <button
          type="button"
          onClick={() => openHistory({ area: 'admin', range: '' })}
          className="inline-flex items-center gap-1 rounded font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="settings-view-changes"
        >
          <History className="h-[13px] w-[13px]" aria-hidden="true" /> View changes
        </button>
      </p>
    ) : null,
    access: (
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          // 28 #6: jump to the preview card under the matrix and put the focus on its role picker
          const card = document.getElementById('access-preview');
          card?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          document.getElementById('access-preview-role')?.focus({ preventScroll: true });
        }}
        data-testid="access-preview-jump"
      >
        <Eye aria-hidden="true" /> Preview as role
      </Button>
    ),
    users: (
      <Button type="button" onClick={() => setAddUserOpen(true)} data-testid="add-user-btn">
        <UserPlus aria-hidden="true" /> Add user
      </Button>
    ),
    stages: stagesEditing || !stageTemplate ? null : (
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" onClick={() => setStageCommand({ type: 'add', n: Date.now() })} data-testid="stages-add-stage">
          <Plus aria-hidden="true" /> Add stage
        </Button>
        <Button type="button" variant={stageOutOfDate ? 'outline' : 'primary'} onClick={() => setStageCommand({ type: 'edit', n: Date.now() })} data-testid="stages-edit-pipeline">
          <SquarePen aria-hidden="true" /> Edit pipeline
        </Button>
      </div>
    ),
    history: (
      <Button type="button" variant="outline" onClick={() => setHistoryExport((n) => n + 1)} data-testid="history-export">
        <Download aria-hidden="true" /> Export CSV
      </Button>
    ),
    integrations: (
      <Button type="button" variant="outline" onClick={recheckIntegrations} disabled={quickBooksLoading || integrationsLoading} data-testid="integrations-recheck">
        <RefreshCw className={cn((quickBooksLoading || integrationsLoading) && 'animate-spin')} aria-hidden="true" /> Recheck status
      </Button>
    ),
  };

  const tabs = [
    { value: 'settings', label: 'Settings', icon: SlidersHorizontal, testId: 'tab-settings-admin' },
    {
      value: 'users', label: 'Users', icon: Users, testId: 'tab-users', count: usersLoading ? null : users.length,
      warn: healthWarn ? `${health.testAdmins.length} test ${health.testAdmins.length === 1 ? 'account has' : 'accounts have'} Admin rights` : '',
    },
    { value: 'access', label: 'Access', icon: ShieldCheck, testId: 'tab-access-admin', count: roleAccess.roles.length },
    {
      value: 'stages', label: 'Stage Templates', icon: Workflow, testId: 'tab-stages-admin',
      count: stageTemplate?.stages?.length ?? null, warn: stageOutOfDate ? 'Template is out of date' : '',
    },
    { value: 'history', label: 'History', icon: History, testId: 'tab-history-admin' },
    {
      value: 'integrations', label: 'Integrations', icon: Plug, testId: 'tab-integrations-admin',
      count: quickBooksStatus ? `${integrationsConnected}/2` : null,
      warn: integrationsWarn,
    },
  ];

  return (
    <div data-testid="admin-page" className="min-w-0 space-y-4">
      {previewTheme ? <PreviewRibbon theme={previewTheme} onExit={stopPreview} /> : null}
      <AdminCrumbs label={tabs.find((item) => item.value === tab)?.label} />
      <PageHeader
        title="Admin"
        subtitle={`Company settings, people & access, workflow templates and connected apps for ${appName}.`}
        actions={headerActions[tab] || null}
      />

      <Tabs value={tab} onValueChange={switchTab} className="w-full min-w-0">
        <div className="flex items-end border-b border-line">
          <div className="relative min-w-0 flex-1">
          <TabsList
            ref={tabListRef}
            className="flex h-auto w-full min-w-0 justify-start gap-4 overflow-x-auto overflow-y-hidden rounded-none bg-transparent p-0 text-ink-3 [scrollbar-width:none] xl:gap-[22px] [&::-webkit-scrollbar]:hidden"
            style={tabMask(tabEdges) ? { maskImage: tabMask(tabEdges), WebkitMaskImage: tabMask(tabEdges) } : undefined}
            data-edges={[tabEdges.left && 'left', tabEdges.right && 'right'].filter(Boolean).join(' ') || undefined}
          >
            {tabs.map((item) => {
              const Icon = item.icon;
              return (
                <TabsTrigger key={item.value} value={item.value} className={tabTriggerClass} data-testid={item.testId}>
                  <Icon aria-hidden="true" />
                  {item.label}
                  {item.count !== null && item.count !== undefined ? <span className={tabCountClass}>{item.count}</span> : null}
                  {item.warn ? <TabWarn title={item.warn} /> : null}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {tabEdges.right ? (
            <button
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              onClick={() => tabListRef.current?.scrollBy?.({ left: 160, behavior: 'smooth' })}
              className="absolute right-0 top-1/2 hidden h-7 w-7 -translate-y-1/2 place-items-center rounded-full border border-line bg-surface text-ink-3 shadow-card hover:text-ink md:grid"
              data-testid="admin-tabs-more"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          ) : null}
          </div>
          <AttentionChip tabs={tabs} onPick={switchTab} />
        </div>

        <TabsContent value="settings" className="mt-4">
          <SettingsTab onDirtyChange={setSettingsDirty} />
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <UsersTab
            users={users}
            setUsers={setUsers}
            loading={usersLoading}
            roles={USER_ROLE_OPTIONS}
            currentUser={currentUser}
            refreshUsers={refreshUsers}
            onPeopleChanged={onPeopleChanged}
            rolePermissions={roleAccess.permissions}
            signInOffEnforced={roleAccess.sign_in_off_enforced}
            signInTrackingSince={roleAccess.sign_in_tracking_since}
            onViewActivity={(person, options) => openHistory({ person: person.id, range: options?.range ?? '' })}
            healthDismissed={healthDismissed}
            onHealthDismiss={dismissHealth}
          />
        </TabsContent>

        <TabsContent value="access" className="mt-4">
          <AccessTab roleAccess={roleAccess} users={users} refreshRoles={refreshRoles} onDirtyChange={setAccessDirty} />
        </TabsContent>

        <TabsContent value="stages" className="mt-4">
          <StagesTab
            stageTemplate={stageTemplate}
            setStageTemplate={setStageTemplate}
            loading={stageLoading}
            command={stageCommand}
            onEditingChange={setStagesEditing}
            onDirtyChange={setStagesDirty}
          />
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <HistoryTab filters={historyFilters} setFilters={setHistoryFilters} users={users} exportCommand={historyExport} />
        </TabsContent>

        <TabsContent value="integrations" className="mt-4">
          <IntegrationsTab
            status={quickBooksStatus}
            loading={quickBooksLoading}
            error={quickBooksError}
            users={users}
            appName={appName}
            onRefresh={recheckIntegrations}
            overview={integrations}
            overviewLoading={integrationsLoading}
            recheck={recheckSeq}
          />
        </TabsContent>
      </Tabs>

      <AddUserPanel
        open={addUserOpen}
        onOpenChange={setAddUserOpen}
        roles={USER_ROLE_OPTIONS}
        permissions={roleAccess.permissions}
        users={users}
        canAssignSuperAdmin={viewerIsSuperAdmin}
        onCreated={(created, { copied = false, announce = true, refresh = true } = {}) => {
          // A blocked copy keeps the panel open on the password: the list refreshes now, the toast comes when
          // the panel closes and says whether the password was copied by hand.
          if (announce) {
            const undoId = created?.undo_id;
            const name = created?.name || 'User';
            (copied ? notify.success : notify.warning)(`${name} added · password ${copied ? 'copied to clipboard' : 'not copied'}`, {
              description: [
                created?.role ? `Role: ${created.role}` : '',
                copied ? 'Share the password with them privately.' : 'Nobody has the password — a Super Admin can reset it from the ⋯ menu.',
              ].filter(Boolean).join(' · '),
              ...(undoId ? {
                undo: async () => {
                  await api.post(`/undo/entries/${undoId}`);
                  await refreshUsers();
                  onPeopleChanged();
                },
                undoneMessage: `${name} removed again`,
              } : {}),
            });
          }
          if (refresh) {
            refreshUsers();
            onPeopleChanged();
            if (tab !== 'users') switchTab('users');
          }
        }}
      />
    </div>
  );
}
